import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Grid from '../../components/Grid';
import messages from './messages';
import Dropdown from '../../components/Dropdown';
import { addCommas, removeCommas, validateValues } from './utils';
import './styles.css';
import Label from '../../components/Label';
import Icon from '../../components/Icon';

const awardTypeOptions = [
  { text: 'Fixed Amount', value: 'FIXED_AMOUNT' },
  { text: 'Range', value: 'RANGE' },
  { text: 'Full Tuition', value: 'FULL_TUITION' },
];

const getMinAmountErrors = (blurred, awardErrors, requiredFieldsMissing, forceShowErrors) => {
  if ((forceShowErrors || blurred.awAmountMin) &&
      (awardErrors.awardAmountMinError || requiredFieldsMissing.includes('awAmountMin'))) {
    return requiredFieldsMissing.includes('awAmountMin') ? (<FormattedMessage {...messages.meritScholarshipsRequiredField} />): awardErrors.awardAmountMinError;
  }
  return null;
};

const getMaxAmountErrors = (blurred, awardErrors, requiredFieldsMissing, forceShowErrors) => {
  if (!getMinAmountErrors(blurred, awardErrors, requiredFieldsMissing, forceShowErrors)) {
    if ((forceShowErrors || blurred.awAmountMax) && (awardErrors.awardAmountMaxError || requiredFieldsMissing.includes('awAmountMax'))) {
      return requiredFieldsMissing.includes('awAmountMax') ? (<FormattedMessage {...messages.meritScholarshipsRequiredField} />) : awardErrors.awardAmountMaxError;
    }
  }
  return null;
};

const getRangeErrors = (blurred, awardErrors, requiredFieldsMissing, forceShowErrors) => {
  if (getMinAmountErrors(blurred, awardErrors, requiredFieldsMissing, forceShowErrors) ||
      getMaxAmountErrors(blurred, awardErrors, requiredFieldsMissing, forceShowErrors)) {
    return null;
  }

  return awardErrors.awardAmountRangeError;
};

const localOnChange = (e, name, index, onChange, changed, setChanged) => {
  const { value } = e.target;
  setChanged(Object.assign(changed, { [name]: value }));
  onChange({ target: { value } }, name);
};

const onBlur = (e, name, index, intl, onChange, blurred, setBlurred, changed, setChanged, awardErrors, setAwardErrors) => {
  blurred[name] = true;
  setBlurred(Object.assign(blurred, { [name]: true }));
  /* istanbul ignore else */
  if (changed[name] !== undefined) {
    setAwardErrors(validateValues(intl, changed.awAmountMin, changed.awAmountMax, 1, 999999999, 'Award Amount') || {});
    const value = changed[name];
    onChange({ target: { value } }, name);
  }
};
/* istanbul ignore next */
const onFocus = (e, name, index, blurred, setBlurred, changed, setChanged) => {
  // changed[name] = e.target.value;
  setChanged(Object.assign(changed, { [name]: e.target.value }));
  setBlurred(Object.assign(blurred, { [name]: false }));
};

const AwardAmount = ({ intl, index, awardType, awAmountMin, awAmountMax, onChange, errors, forceShowErrors }) => {
  const [blurred, setBlurred] = useState({ awAmountMin: false, awAmountMax: false });
  const [changed, setChanged] = useState({ awAmountMin, awAmountMax });
  const [awardErrors, setAwardErrors] = useState({});

  const minErrors = getMinAmountErrors(blurred, awardErrors, errors.requiredFieldsMissing, forceShowErrors);
  const maxErrors = getMaxAmountErrors(blurred, awardErrors, errors.requiredFieldsMissing, forceShowErrors);
  const rangeErrors = getRangeErrors(blurred, awardErrors, errors.requiredFieldsMissing, forceShowErrors);
  const isMinRequiredError = errors.requiredFieldsMissing.includes('awAmountMin');
  const isMaxRequiredError = errors.requiredFieldsMissing.includes('awAmountMax');

  const maxAmount = Number(changed.awAmountMax) === 0 ? undefined : changed.awAmountMax;

  return (
    <React.Fragment key={index}>
      <Grid.Row>
        <Grid.Column width={3}>
          <b><FormattedMessage {...messages.meritScholarshipsAwardAmountLabel} /></b>
          <span styleName="required">&#x002a;</span>
        </Grid.Column>
        <Grid.Column largeScreen={2} computer={3} tablet={3} mobile={8} styleName="merit-scholarship-award-type-column">
          <Dropdown
            key={`merit-scholarships-award-type-dropdown-${index}`}
            data-cy="merit-scholarships-award-type-dropdown"
            aria-label="Choose type of award"
            styleName="merit-scholarships-award-type-dropdown"
            value={awardType}
            options={awardTypeOptions.map((o) => ({ ...o, 'aria-label': o.value }))}
            onChange={(e, { value }) => onChange({ target: { value } }, 'awardType')}
          />
        </Grid.Column>
        <Grid.Column styleName="merit-scholarships-award-column">
          {awardType !== 'FULL_TUITION' && (
            <React.Fragment>
              <span>&nbsp;$&nbsp;</span>
              <div styleName="merit-scholarships-award-min-amount-input-container">
                <input
                  id={`merit-scholarships-award-min-amount-${index}`}
                  key={`merit-scholarships-award-min-amount-${index}`}
                  data-cy="merit-scholarships-award-min-amount"
                  aria-label="Minimun amount of award"
                  styleName="merit-scholarships-award-type-input"
                  value={blurred.awAmountMin ? addCommas(removeCommas(changed.awAmountMin)) : changed.awAmountMin}
                  onChange={(e) => localOnChange(e, 'awAmountMin', index, onChange, changed, setChanged)}
                  onBlur={(e) => onBlur(e, 'awAmountMin', index, intl, onChange, blurred, setBlurred, changed, setChanged, awardErrors, setAwardErrors)}
                  onFocus={(e) => onFocus(e, 'awAmountMin', index, blurred, setBlurred, changed, setChanged)}
                  required
                />
                {minErrors && (
                  <Label styleName={`merit-scholarships-scholarships-award-min-error ${isMinRequiredError ? 'required' : ''}`} role="tooltip" basic color="red" pointing data-cy="merit-scholarships-scholarships-award-error">
                    <Icon name="exclamation circle" styleName="validationError" size="large" />
                    {minErrors}
                  </Label>
                )}
              </div>
            </React.Fragment>
          )}
          {awardType === 'RANGE' && (
            <React.Fragment>
              <span>&nbsp;to $&nbsp;</span>
              <div styleName="merit-scholarships-award-max-amount-input-container">
                <input
                  id={`merit-scholarships-award-max-amount-${index}`}
                  key={`merit-scholarships-award-max-amount-${index}`}
                  data-cy="merit-scholarships-award-max-amount"
                  aria-label="Maximum amount of award"
                  styleName="merit-scholarships-award-type-input"
                  value={blurred.awAmountMax ? addCommas(removeCommas(maxAmount)) : maxAmount}
                  onChange={(e) => localOnChange(e, 'awAmountMax', index, onChange, changed, setChanged)}
                  onBlur={(e) => onBlur(e, 'awAmountMax', index, intl, onChange, blurred, setBlurred, changed, setChanged, awardErrors, setAwardErrors)}
                  onFocus={(e) => onFocus(e, 'awAmountMax', index, blurred, setBlurred, changed, setChanged)}
                  required
                />
                {maxErrors && (
                  <Label styleName={`merit-scholarships-scholarships-award-max-error ${isMaxRequiredError ? 'required' : ''}`} role="tooltip" basic color="red" pointing data-cy="merit-scholarships-scholarships-award-error">
                    <Icon name="exclamation circle" styleName="validationError" size="large" />
                    {maxErrors}
                  </Label>
                )}
              </div>
            </React.Fragment>
          )}
          {awardType === 'RANGE' && rangeErrors && (
            <Label styleName="merit-scholarships-scholarships-award-range-error" role="tooltip" basic color="red" pointing data-cy="merit-scholarships-scholarships-award-error">
              <Icon name="exclamation circle" styleName="validationError" size="large" />
              {rangeErrors}
            </Label>
          )}
        </Grid.Column>
      </Grid.Row>
    </React.Fragment>
  );
};

AwardAmount.propTypes = {
  index: PropTypes.string,
  awardType: PropTypes.string,
  awAmountMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  awAmountMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  errors: PropTypes.shape({
    awardAmountError: PropTypes.string,
    requiredFieldsMissing: PropTypes.arrayOf(PropTypes.string),
  }),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
  forceShowErrors: PropTypes.bool,
};

export default AwardAmount;
